import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useReducer,
} from "react";
import { useAppContext } from "../../../helpers/context-hooks/useAppContext";
import { defaultAlertMessageState } from "./constants";
import { flattenMessagingState, setMessageStatuses } from "./helpers";
import type { FlattenedMessageState, MessagesState } from "./types";
import type { AppState } from "../../../@types/context";
import { exhaustiveGuard } from "../../store/reducer-helpers/guard";

/**
 * ! Do not add to the pattern in this file. Put efforts towards refactoring coachmarks instead.
 * @see MCT-11538
 * */

enum AlertActionType {
  LowestCostPharmacyParametersUpdated,
}

interface AlertAction {
  payload: AppState;
  type: AlertActionType;
}

type Dispatch = (action: AlertAction) => void;

type AlertProviderProps = {
  children: ReactNode;
};

const AlertMessageContext = createContext<
  | {
      state: FlattenedMessageState;
      dispatch: Dispatch;
    }
  | undefined
>(undefined);

function alertMessageReducer(
  state: MessagesState,
  action: AlertAction
): MessagesState {
  const { payload, type } = action;
  switch (type) {
    case AlertActionType.LowestCostPharmacyParametersUpdated: {
      return setMessageStatuses({
        messageState: state,
        appState: payload,
      });
    }
    default: {
      // We should never reach this default case
      return exhaustiveGuard(type, payload);
    }
  }
}

function AlertMessageProvider({ children }: AlertProviderProps) {
  const { state: appState } = useAppContext();
  const [state, dispatch] = useReducer(
    alertMessageReducer,
    {
      messages: defaultAlertMessageState,
    },
    initialState =>
      setMessageStatuses({
        messageState: initialState,
        appState,
      })
  );

  useEffect(() => {
    dispatch({
      type: AlertActionType.LowestCostPharmacyParametersUpdated,
      payload: appState,
    });
    // ! TODO: See MCT-11398
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This error has been grandfathered in and needs to be fixed.
  }, [
    appState.pharmacies,
    appState.pharmacyType,
    appState.beneficiary,
    appState.prescriptions,
    appState.mailOrderNetworkStatus,
    appState.routeParams,
  ]);

  const value = {
    state: {
      messages: flattenMessagingState(state.messages),
    },
    dispatch,
  };

  return (
    <AlertMessageContext.Provider value={value}>
      {children}
    </AlertMessageContext.Provider>
  );
}

function useAlertMessaging() {
  const context = useContext(AlertMessageContext);
  if (context === undefined) {
    throw new Error(
      "useAlertMessaging must be used within a AlertMessageProvider"
    );
  }
  return context;
}

export {
  /** @deprecated -- do not add to this pattern. Put efforts towards refactoring coachmarks instead. @see MCT-11538  */
  AlertMessageProvider,
  /** @deprecated -- do not add to this pattern. Put efforts towards refactoring coachmarks instead. @see MCT-11538  */
  useAlertMessaging,
};
